<script setup>
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ChecklistChangeList from '@/components/admin/report-qc/ChecklistChangeList.vue'

/**
 * RED-385: Kontrollnimekirja versioonid. Newest first: vN, an info icon whose tooltip
 * holds the hash the runs store (RED-361), date · author, the note, `kehtiv` or how many checks used it,
 * and what changed against the version before (Üldjuhis section, checklist
 * item, question to the engine). `Taasta see versioon` saves the old content
 * as a NEW version.
 *
 * RED-361 (dev → live): `Laadi alla` on every version downloads it as a file;
 * `Laadi üles` reads such a file, checks its format and hands it to the editor
 * (`import`), which loads it into the unsaved draft. Nothing is saved here.
 */
const props = defineProps({
  versions: { type: Array, default: undefined },
  // `{ format, formatVersion }` of the file, from the server (`ChecklistExport`).
  transfer: { type: Object, required: true },
})

const emit = defineEmits(['import'])

const visible = defineModel('visible', { type: Boolean, default: false })

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const fileInput = ref(null)

const dateTime = iso =>
  iso ? new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

// Why a file cannot be loaded, or null when it can. The save rules are the server's (the preview).
const fileProblem = file => {
  if (!isObject(file) || file.format !== props.transfer.format) {
    return 'import_err_format'
  }
  if (file.formatVersion !== props.transfer.formatVersion) {
    return 'import_err_version'
  }
  const snapshot = file.snapshot
  if (!isObject(snapshot) || !Array.isArray(snapshot.items) || !Array.isArray(snapshot.queries) || !isObject(snapshot.frame) || typeof file.hash !== 'string') {
    return 'import_err_format'
  }

  return null
}

const onFile = async event => {
  const chosen = event.target.files?.[0]
  event.target.value = ''

  if (!chosen) {
    return
  }

  let file
  try {
    file = JSON.parse(await chosen.text())
  } catch {
    file = null
  }

  const problem = fileProblem(file)
  if (problem) {
    toast.add({ severity: 'error', summary: t('messages.admin.report_qc.transfer.import_err_title'), detail: t(`messages.admin.report_qc.transfer.${problem}`), life: 8000 })

    return
  }

  visible.value = false
  emit('import', file)
}

const restore = version => {
  confirm.require({
    header: t('messages.admin.report_qc.history.confirm_header', { n: version.number }),
    message: t('messages.admin.report_qc.checklist_history.confirm_message'),
    icon: 'pi pi-history',
    rejectProps: { label: t('messages.admin.report_qc.history.confirm_reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('messages.admin.report_qc.history.confirm_accept') },
    accept: () => {
      router.post(
        route('admin.report-qc.checklist.restore', version.id),
        {},
        {
          preserveScroll: true,
          onSuccess: page => {
            toast.add({
              severity: 'success',
              summary: t('messages.admin.report_qc.history.restored'),
              detail: t('messages.admin.report_qc.checklist_history.restored_detail', {
                old: version.number,
                new: page.props.checklist.versionNumber,
              }),
              life: 4000,
            })
          },
          onError: errors => {
            toast.add({ severity: 'error', summary: t('messages.error'), detail: errors.restore, life: 6000 })
          },
        },
      )
    },
  })
}
</script>

<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :header="t('messages.admin.report_qc.checklist_history.title')"
    class="w-full! md:w-[36rem]!"
    data-testid="checklist-history"
  >
    <section class="mb-4 flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3" data-testid="checklist-transfer">
      <p class="text-sm text-gray-700">{{ t('messages.admin.report_qc.transfer.intro') }}</p>
      <div>
        <Button
          :label="t('messages.admin.report_qc.transfer.import')"
          icon="pi pi-upload"
          size="small"
          severity="secondary"
          outlined
          class="w-auto!"
          data-testid="checklist-import"
          @click="fileInput?.click()"
        />
        <input ref="fileInput" type="file" accept=".json,application/json" class="hidden" data-testid="checklist-import-input" @change="onFile" />
      </div>
    </section>

    <div v-if="versions === undefined" class="flex flex-col gap-3">
      <Skeleton v-for="n in 3" :key="n" height="5rem" />
    </div>

    <p v-else-if="versions.length === 0" class="text-sm text-gray-600">
      {{ t('messages.admin.report_qc.uldjuhis.no_version_yet') }}
    </p>

    <ol v-else class="flex flex-col gap-4">
      <li
        v-for="version in versions"
        :key="version.id"
        class="rounded-md border border-gray-200 p-3"
        :data-testid="`checklist-history-v${version.number}`"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="font-mono font-medium">v{{ version.number }}</span>
          <i v-if="version.hash" v-tooltip.top="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: version.hash })" class="pi pi-info-circle ml-1 text-xs text-gray-400" :aria-label="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: version.hash })" data-testid="checklist-history-hash" />
          <Tag v-if="version.current" :value="t('messages.admin.report_qc.history.current')" severity="success" />
          <!-- RED-387: no done trial (and no bench baseline) for this hash yet. -->
          <Tag
            v-if="version.tested === false"
            v-tooltip.top="{ value: t('messages.admin.report_qc.trial.untested_tooltip'), showDelay: 300, pt: { root: { style: { maxWidth: '360px' } } } }"
            :value="t('messages.admin.report_qc.trial.untested')"
            severity="secondary"
            :data-testid="`checklist-history-untested-v${version.number}`"
          />
          <span class="text-xs text-gray-500">{{ t('messages.admin.report_qc.history.used', { n: version.usedInChecks }) }}</span>
        </div>
        <p class="mt-1 text-xs text-gray-500">
          {{ dateTime(version.createdAt) }} · {{ version.author ?? t('messages.admin.report_qc.history.author_system') }}
        </p>
        <p class="mt-2 text-sm whitespace-pre-line">{{ version.note }}</p>
        <p v-if="version.sameAs" class="mt-1 text-xs text-gray-500">
          {{ t('messages.admin.report_qc.checklist_history.same_as', { n: version.sameAs }) }}
        </p>

        <p v-if="version.changes === null" class="mt-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.history.first_version') }}</p>
        <p v-else-if="version.changes.length === 0" class="mt-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.history.no_changes') }}</p>
        <ChecklistChangeList v-else :changes="version.changes" class="mt-2" />

        <div class="mt-3 flex flex-wrap justify-end gap-2">
          <Button
            as="a"
            :href="route('admin.report-qc.checklist.export', version.id)"
            download
            :label="t('messages.admin.report_qc.transfer.export')"
            icon="pi pi-download"
            size="small"
            severity="secondary"
            text
            :data-testid="`checklist-export-v${version.number}`"
          />
          <Button
            v-if="!version.current"
            :label="t('messages.admin.report_qc.history.restore')"
            icon="pi pi-history"
            size="small"
            severity="secondary"
            outlined
            :data-testid="`checklist-restore-v${version.number}`"
            @click="restore(version)"
          />
        </div>
      </li>
    </ol>
  </Drawer>
</template>
